import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Printer } from "lucide-react";
import { api, formatApiError, gbp } from "@/lib/api";

export default function AdminPackingSheet() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/production/packing/${orderId}`).then((r) => setData(r.data)).catch((e) => { toast.error(formatApiError(e)); setData(false); });
  }, [orderId]);

  if (data === null) return <p className="text-sm text-cream-muted">Loading…</p>;
  if (data === false) return <p className="text-sm text-red-400">Packing sheet not available.</p>;

  const o = data.order;
  const slot = o.slot_snapshot || {};
  const address = o.address_snapshot;

  return (
    <div data-testid="packing-sheet-page">
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <Link to="/admin/production" className="text-xs font-bold text-gold hover:underline" data-testid="back-to-production">← Production</Link>
        <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-full bg-gold px-4 py-2 text-xs font-bold text-choco" data-testid="packing-print-button">
          <Printer className="h-3.5 w-3.5" aria-hidden="true" /> Print packing sheet
        </button>
      </div>

      <div className="print-sheet mt-4 rounded-3xl border border-choco-line bg-choco-light p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-choco-line pb-4">
          <div>
            <h1 className="font-serif text-2xl font-bold text-cream-text" data-testid="packing-order-number">{o.order_number}</h1>
            <p className="mt-1 text-sm text-cream-muted">{o.customer?.name} · {o.customer?.phone}</p>
          </div>
          <div className="text-right text-sm text-cream-muted">
            <p className="font-bold capitalize text-gold">{o.fulfilment_method}</p>
            <p>{slot.date} · {slot.start_time}–{slot.end_time}</p>
            {o.payment_status === "unpaid_on_collection" && (
              <p className="mt-1 inline-block rounded-full bg-amber-500/20 px-3 py-1 text-xs font-bold text-amber-300" data-testid="packing-amount-due">Collect {gbp(o.total_pence)} on collection</p>
            )}
          </div>
        </div>

        {o.fulfilment_method === "delivery" && address && (
          <p className="mt-4 text-sm text-cream-muted" data-testid="packing-address">
            Deliver to: <span className="font-bold text-cream-text">{address.line1}{address.line2 ? `, ${address.line2}` : ""}, {address.city}, {address.postcode}</span>
          </p>
        )}
        {o.notes && <p className="mt-2 text-sm text-cream-muted">Customer notes: <span className="text-cream-text">{o.notes}</span></p>}

        <table className="mt-6 w-full text-left text-sm" data-testid="packing-items">
          <thead>
            <tr className="text-[11px] uppercase tracking-widest text-cream-muted">
              <th className="pb-2 pr-3 font-semibold">Qty</th>
              <th className="pb-2 pr-3 font-semibold">Item</th>
              <th className="pb-2 font-semibold">Allergens</th>
            </tr>
          </thead>
          <tbody>
            {data.items_with_food_info.map((item, i) => (
              <tr key={i} className="border-t border-choco-line align-top">
                <td className="py-2.5 pr-3 font-serif text-lg font-bold text-cream-text">{item.qty}</td>
                <td className="py-2.5 pr-3 text-cream-text">
                  {item.name}
                  {Object.keys(item.option_values || {}).length > 0 && (
                    <span className="block text-xs text-cream-muted">{Object.entries(item.option_values).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>
                  )}
                </td>
                <td className="py-2.5 text-xs capitalize text-cream-muted">
                  {item.allergen_declaration_status === "none_of_14_as_ingredients"
                    ? "No UK 14 regulated allergens as ingredients"
                    : item.allergens?.length ? item.allergens.join(", ") : "—"}
                  {item.may_contain?.length > 0 && (
                    <span className="block text-amber-300" data-testid={`packing-may-contain-${i}`}>May contain: {item.may_contain.join(", ")}</span>
                  )}
                  {!item.allergens_verified && (
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold normal-case text-amber-300">
                      <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Unverified
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 className="mt-8 font-serif text-lg font-semibold text-cream-text">Draft allergen &amp; ingredient labels</h2>
        <p className="mt-1 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-bold text-amber-300" data-testid="packing-label-notice">{data.label_notice}</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {data.items_with_food_info.map((item, i) => (
            <div key={i} className="rounded-2xl border border-choco-line p-4" data-testid={`packing-label-${i}`}>
              <p className="font-serif text-base font-bold text-cream-text">{item.name}</p>
              {Object.keys(item.option_values || {}).length > 0 && (
                <p className="text-xs text-cream-muted">{Object.entries(item.option_values).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
              )}
              <p className="mt-2 text-xs text-cream-muted"><span className="font-bold text-cream-text">Ingredients:</span> {item.ingredients || "Not recorded"}</p>
              <p className="mt-1 text-xs text-cream-muted"><span className="font-bold text-cream-text">Allergens:</span> <span className="font-bold capitalize" data-testid={`packing-label-allergens-${i}`}>
                {item.allergen_declaration_status === "none_of_14_as_ingredients"
                  ? "No UK 14 regulated allergens are used as ingredients."
                  : item.allergens?.length ? item.allergens.join(", ") : "Not reviewed — do not use"}
              </span></p>
              {item.may_contain?.length > 0 && (
                <p className="mt-1 text-xs text-cream-muted"><span className="font-bold text-cream-text">May contain:</span> <span className="font-bold capitalize">{item.may_contain.join(", ")}</span></p>
              )}
              {item.storage_instructions && <p className="mt-1 text-xs text-cream-muted"><span className="font-bold text-cream-text">Storage:</span> {item.storage_instructions}</p>}
              {item.shelf_life && <p className="mt-1 text-xs text-cream-muted"><span className="font-bold text-cream-text">Shelf life:</span> {item.shelf_life}</p>}
              <p className="mt-2 text-[10px] uppercase tracking-widest text-amber-300/80">Draft — review before use</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
